import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { groupPendingDocuments, PendingGroup } from './pendingGroups';
import type { QueueDocument } from './useDashboardData';

/**
 * Internal review target in calendar days. Documents waiting longer than this
 * count as "fuera de objetivo". It is a product setting, not a legal deadline.
 */
export const REVIEW_TARGET_DAYS = 3;
const DAY = 86400000;
const CHUNK = 150;
// document_reviews is not in the generated types yet.
const db = supabase as unknown as SupabaseClient;

export type AiRecommendation = 'recommend_approve' | 'recommend_reject' | 'uncertain';

export interface AgingBucket {
  id: string;
  label: string;
  count: number;
  tone: 'ok' | 'watch' | 'late' | 'critical';
}

export interface PriorityGroup extends PendingGroup<QueueDocument> {
  ageDays: number;
  resubmission: boolean;
  ai: AiRecommendation | null;
}

export interface AiAgreement {
  /** Decisions whose AI review existed before the human decision. */
  total: number;
  agreed: number;
  /** AI recommended rejecting and the reviewer approved. */
  approvedAgainstAi: number;
  /** AI recommended approving and the reviewer rejected. */
  rejectedAgainstAi: number;
  uncertain: number;
}

export interface ReviewerLoad {
  id: string;
  name: string;
  approved: number;
  rejected: number;
}

export interface WorkloadData {
  toReview: number;
  waitingOnSupplier: number;
  oldestDays: number | null;
  pastTarget: number;
  aging: AgingBucket[];
  priority: PriorityGroup[];
  decidedThisWeek: { approved: number; rejected: number; mine: number };
  ai: AiAgreement;
  team: ReviewerLoad[];
}

export interface Decision {
  id: string;
  status: string;
  approval_timestamp: string;
  upload_date: string;
  approved_by_user_id: string | null;
}

export interface AiReviewRow {
  document_id: string;
  created_at: string;
  recommendation: string | null;
}

export const ageInDays = (from: string, now: Date) => {
  const start = Date.parse(from);
  return Number.isFinite(start) ? Math.max(0, Math.floor((now.getTime() - start) / DAY)) : 0;
};

export function summarizeAging(uploadDates: string[], now: Date): AgingBucket[] {
  const buckets: AgingBucket[] = [
    { id: '0-2', label: '0–2 días', count: 0, tone: 'ok' },
    { id: '3-7', label: '3–7 días', count: 0, tone: 'watch' },
    { id: '8-30', label: '8–30 días', count: 0, tone: 'late' },
    { id: '30+', label: 'Más de 30 días', count: 0, tone: 'critical' },
  ];
  uploadDates.forEach((date) => {
    const age = ageInDays(date, now);
    const index = age <= 2 ? 0 : age <= 7 ? 1 : age <= 30 ? 2 : 3;
    buckets[index].count++;
  });
  return buckets;
}

const periodKey = (month: string | null | undefined, year: number | null | undefined) => `${month || ''}-${year || ''}`;

/**
 * Orders the reviewer's work: anything past the review target first (oldest
 * first), then suppliers who corrected a rejected document (closing that loop
 * restores their evidence fastest), then the rest by age.
 */
export function prioritize(
  queue: QueueDocument[],
  previousRejections: Array<{ requirement_id: string; applicable_month: string | null; applicable_year: number | null; upload_date: string }>,
  reviews: Map<string, AiRecommendation>,
  now: Date,
): PriorityGroup[] {
  const rejected = new Map<string, string[]>();
  previousRejections.forEach((row) => {
    const key = `${row.requirement_id}:${periodKey(row.applicable_month, row.applicable_year)}`;
    rejected.set(key, [...(rejected.get(key) || []), row.upload_date]);
  });
  const isResubmission = (doc: QueueDocument) =>
    (rejected.get(`${doc.requirement_id}:${periodKey(doc.applicable_month, doc.applicable_year)}`) || []).some(
      (date) => Date.parse(date) < Date.parse(doc.upload_date),
    );
  return groupPendingDocuments(queue)
    .map((group) => {
      const oldestUpload = group.documents.reduce(
        (min, doc) => (Date.parse(doc.upload_date) < Date.parse(min) ? doc.upload_date : min),
        group.oldest.upload_date,
      );
      const recs = group.documents.map((doc) => reviews.get(doc.document_id)).filter(Boolean) as AiRecommendation[];
      const ai = recs.includes('recommend_reject') ? 'recommend_reject' : recs.includes('uncertain') ? 'uncertain' : recs[0] || null;
      return { ...group, ageDays: ageInDays(oldestUpload, now), resubmission: group.documents.some(isResubmission), ai };
    })
    .sort((a, b) => {
      const late = Number(b.ageDays > REVIEW_TARGET_DAYS) - Number(a.ageDays > REVIEW_TARGET_DAYS);
      return late || (a.ageDays > REVIEW_TARGET_DAYS ? 0 : Number(b.resubmission) - Number(a.resubmission)) || b.ageDays - a.ageDays;
    });
}

export function summarizeAiAgreement(decisions: Decision[], reviews: AiReviewRow[]): AiAgreement {
  const latestBefore = new Map<string, AiReviewRow>();
  const decidedAt = new Map(decisions.map((d) => [d.id, Date.parse(d.approval_timestamp)]));
  reviews.forEach((review) => {
    const decided = decidedAt.get(review.document_id);
    const created = Date.parse(review.created_at);
    if (decided === undefined || !(created < decided)) return;
    const current = latestBefore.get(review.document_id);
    if (!current || Date.parse(current.created_at) < created) latestBefore.set(review.document_id, review);
  });
  const result: AiAgreement = { total: 0, agreed: 0, approvedAgainstAi: 0, rejectedAgainstAi: 0, uncertain: 0 };
  decisions.forEach((decision) => {
    const review = latestBefore.get(decision.id);
    if (!review || (decision.status !== 'approved' && decision.status !== 'rejected')) return;
    result.total++;
    if (review.recommendation === 'uncertain') result.uncertain++;
    else if (review.recommendation === 'recommend_approve') decision.status === 'approved' ? result.agreed++ : result.rejectedAgainstAi++;
    else if (review.recommendation === 'recommend_reject') decision.status === 'rejected' ? result.agreed++ : result.approvedAgainstAi++;
  });
  return result;
}

export function summarizeTeam(decisions: Decision[], names: Map<string, string>, since: Date): ReviewerLoad[] {
  const byReviewer = new Map<string, ReviewerLoad>();
  decisions.forEach((decision) => {
    if (!decision.approved_by_user_id || Date.parse(decision.approval_timestamp) < since.getTime()) return;
    const id = decision.approved_by_user_id;
    const row = byReviewer.get(id) || { id, name: names.get(id) || 'Revisor', approved: 0, rejected: 0 };
    if (decision.status === 'approved') row.approved++;
    if (decision.status === 'rejected') row.rejected++;
    byReviewer.set(id, row);
  });
  return Array.from(byReviewer.values()).sort((a, b) => b.approved + b.rejected - (a.approved + a.rejected));
}

const chunks = <T,>(items: T[]) => Array.from({ length: Math.ceil(items.length / CHUNK) }, (_, i) => items.slice(i * CHUNK, (i + 1) * CHUNK));

// Same population the review queue uses: active, non-internal suppliers.
const SUPPLIER_JOIN =
  'user_document_requirements!documents_requirement_id_fkey!inner(users!user_document_requirements_user_id_fkey!inner(is_internal,is_active,is_deleted))';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onlySuppliers(query: any) {
  return query
    .eq('user_document_requirements.users.is_internal', false)
    .eq('user_document_requirements.users.is_active', true)
    .eq('user_document_requirements.users.is_deleted', false);
}

async function fetchAiReviews(documentIds: string[]): Promise<AiReviewRow[]> {
  const rows: AiReviewRow[] = [];
  for (const ids of chunks(documentIds)) {
    const { data, error } = await db
      .from('document_reviews')
      .select('document_id,created_at,recommendation:assessment->>recommendation')
      .in('document_id', ids);
    if (error) throw error;
    rows.push(...((data || []) as unknown as AiReviewRow[]));
  }
  return rows;
}

export async function getWorkloadData(currentUserId: string | null, now = new Date()): Promise<WorkloadData> {
  const { data: queueData, error: queueError } = await supabase.rpc('get_all_documents', {
    filter_status: 'pending',
    limit_param: 1000,
    offset_param: 0,
  });
  if (queueError) throw queueError;
  const queue = (queueData || []) as QueueDocument[];

  const waiting = await onlySuppliers(
    db
      .from('documents')
      .select(`id,${SUPPLIER_JOIN}`, { count: 'exact', head: true })
      .eq('is_deleted', false)
      .eq('status', 'pending_approval')
      .not('rejection_reason', 'is', null),
  );
  if (waiting.error) throw waiting.error;

  const requirementIds = Array.from(new Set(queue.map((doc) => doc.requirement_id)));
  const previous: Array<{ requirement_id: string; applicable_month: string | null; applicable_year: number | null; upload_date: string }> = [];
  for (const ids of chunks(requirementIds)) {
    const { data, error } = await supabase
      .from('documents')
      .select('requirement_id,applicable_month,applicable_year,upload_date')
      .in('requirement_id', ids)
      .not('rejection_reason', 'is', null)
      .neq('status', 'pending_approval');
    if (error) throw error;
    previous.push(...((data || []) as typeof previous));
  }

  const pendingReviews = await fetchAiReviews(queue.map((doc) => doc.document_id));
  const latestRec = new Map<string, { at: number; rec: AiRecommendation }>();
  pendingReviews.forEach((row) => {
    const at = Date.parse(row.created_at);
    const current = latestRec.get(row.document_id);
    if (row.recommendation && (!current || current.at < at)) latestRec.set(row.document_id, { at, rec: row.recommendation as AiRecommendation });
  });

  const since90 = new Date(now.getTime() - 90 * DAY).toISOString();
  const decisions: Decision[] = [];
  for (let offset = 0; offset < 50000; offset += 1000) {
    const { data, error } = await onlySuppliers(
      db
        .from('documents')
        .select(`id,status,approval_timestamp,upload_date,approved_by_user_id,${SUPPLIER_JOIN}`)
        .eq('is_deleted', false)
        .in('status', ['approved', 'rejected'])
        .gte('approval_timestamp', since90),
    )
      .order('id')
      .range(offset, offset + 999);
    if (error) throw error;
    decisions.push(...((data || []) as unknown as Decision[]));
    if (!data || data.length < 1000) break;
  }

  const reviewerIds = Array.from(new Set(decisions.map((d) => d.approved_by_user_id).filter(Boolean))) as string[];
  const names = new Map<string, string>();
  if (reviewerIds.length) {
    const { data, error } = await supabase.from('users').select('id,full_name,email').in('id', reviewerIds);
    if (error) throw error;
    (data || []).forEach((user: { id: string; full_name: string | null; email: string }) => names.set(user.id, user.full_name || user.email));
  }

  const weekAgo = new Date(now.getTime() - 7 * DAY);
  const thisWeek = decisions.filter((d) => Date.parse(d.approval_timestamp) >= weekAgo.getTime());
  const ages = queue.map((doc) => ageInDays(doc.upload_date, now));
  return {
    toReview: queue.length,
    waitingOnSupplier: waiting.count || 0,
    oldestDays: ages.length ? Math.max(...ages) : null,
    pastTarget: ages.filter((age) => age > REVIEW_TARGET_DAYS).length,
    aging: summarizeAging(queue.map((doc) => doc.upload_date), now),
    priority: prioritize(queue, previous, new Map(Array.from(latestRec, ([id, v]) => [id, v.rec])), now),
    decidedThisWeek: {
      approved: thisWeek.filter((d) => d.status === 'approved').length,
      rejected: thisWeek.filter((d) => d.status === 'rejected').length,
      mine: currentUserId ? thisWeek.filter((d) => d.approved_by_user_id === currentUserId).length : 0,
    },
    ai: summarizeAiAgreement(decisions, await fetchAiReviews(decisions.map((d) => d.id))),
    team: summarizeTeam(decisions, names, weekAgo),
  };
}
